import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  ArrowRight, Compass, Wallet, GraduationCap, MapPin, Globe, ShieldCheck, Users, CalendarDays,
} from 'lucide-react';
import { LogoBox, useTenantBrand } from '../context/TenantBrandContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WHAT_THEY_GET = [
  { icon: Compass, title: 'Know what actually fits',
    line: 'A scored, honest read on interests, strengths and the way they like to work, not a personality quiz.' },
  { icon: Wallet, title: 'Price the life first',
    line: 'The life they want, costed out month by month, and the income that pays for it.' },
  { icon: GraduationCap, title: 'A plan with real numbers',
    line: 'Education and training routes with tuition, funding, debt and what the first job pays.' },
];

const fade = (i = 0) => ({
  initial: { opacity: 0, y: 18 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true },
  transition: { duration: 0.5, delay: i * 0.07 },
});

/** The tenant's front door: their logo, their colours, and a way in that doesn't leave their world. */
export default function OrgPortalPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { loadBySlug, palette } = useTenantBrand();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const [code, setCode] = useState('');
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    loadBySlug(slug).then(setData).catch(() => setMissing(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const join = async (value) => {
    const entered = (value || code).trim().toUpperCase();
    if (!entered) return;
    setChecking(true);
    try {
      const r = await axios.get(`${API}/orgs/code/${entered}/check`);
      nav(`/${r.data.organization.slug}/join?code=${entered}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "We don't recognise that code");
    } finally { setChecking(false); }
  };

  if (missing) return <Navigate to="/" replace />;
  if (!data) return <div className="min-h-screen bg-white" />;

  const { organization: org, branding: b, programs } = data;
  const P = palette;

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="org-portal-page">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center gap-4">
          <LogoBox src={b.logo_url} name={b.display_name || org.name} size="sm" className="shadow-none border-0" />
          <span className="font-outfit font-semibold tracking-tight" data-testid="portal-name">
            {b.display_name || org.name}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Link to={`/${slug}/signin`} data-testid="portal-login"
              className="text-sm text-slate-300 hover:text-slate-900 px-4 py-2 rounded-full transition-colors">
              Sign in
            </Link>
            <button onClick={() => document.getElementById('join')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="portal-join"
              className="text-sm font-semibold px-5 py-2 rounded-full transition-transform hover:scale-[1.02]"
              style={{ background: P.primary, color: P.onPrimary }}>
              Join with my code
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="px-5 md:px-8 pt-20 md:pt-28 pb-6">
        <div className="max-w-3xl mx-auto flex flex-col items-center text-center">
          <LogoBox src={b.logo_url} name={org.name} size="lg" />
          <p className="mt-8 text-[11px] uppercase tracking-[0.28em] font-semibold"
            style={{ color: P.ink }}>
            {(org.org_type || '').replace(/_/g, ' ')} · FuturePath portal
          </p>
          <h1 className="mt-4 font-outfit text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.02]">
            {b.portal_title || `Welcome to ${org.name}`}
          </h1>
          <p className="mt-6 text-base md:text-lg text-slate-300 leading-relaxed max-w-2xl text-left sm:text-center"
            data-testid="portal-welcome">
            {b.welcome_message || org.description}
          </p>

          {/* Inline join, nobody leaves their world to find out if a code works */}
          <div id="join" className="w-full max-w-md mt-10 relative" data-testid="portal-join-form">
            <input value={code} onChange={e => setCode(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === 'Enter' && join()}
              placeholder="Enter your code" data-testid="portal-join-input"
              className="h-16 w-full rounded-full text-center text-lg tracking-[0.18em] font-semibold
                         border-2 border-slate-200 focus:outline-none pr-36 transition-colors placeholder:tracking-normal
                         placeholder:font-normal placeholder:text-slate-300"
              style={{ borderColor: code ? P.primary : undefined }} />
            <button onClick={() => join()} disabled={checking || !code.trim()}
              data-testid="portal-join-button"
              className="absolute right-2 top-2 h-12 px-6 rounded-full font-bold text-sm shadow-sm
                         transition-transform hover:-translate-y-0.5 disabled:opacity-50"
              style={{ background: P.primary, color: P.onPrimary }}>
              {checking ? 'Checking…' : 'Start'}
            </button>
          </div>
          <p className="mt-3 text-sm text-slate-300">
            {org.accepting
              ? <>No code? <Link to={`/${slug}/join`} data-testid="portal-start"
                  className="underline decoration-2 underline-offset-2"
                  style={{ textDecorationColor: P.accent }}>ask {org.name} for one</Link>.</>
              : <span data-testid="portal-full">All places are taken right now, ask {org.name} about the next intake.</span>}
          </p>

          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-9 text-sm text-slate-300">
            {org.location && (
              <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {org.location}</span>
            )}
            {org.website && (
              <a href={org.website} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 hover:text-slate-900 transition-colors">
                <Globe className="w-3.5 h-3.5" /> {org.website.replace(/^https?:\/\//, '')}
              </a>
            )}
            {org.ages?.[0] && <span>Ages {org.ages[0]}-{org.ages[1] || 18}</span>}
          </div>
        </div>
      </section>

      {/* What they get */}
      <section className="px-5 md:px-8 py-20 md:py-24">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-outfit text-3xl md:text-4xl font-bold tracking-tighter text-center">
            What {org.name} is giving them access to
          </h2>
          <div className="grid md:grid-cols-12 gap-6 mt-12">
            {WHAT_THEY_GET.map((w, i) => (
              <motion.div key={w.title} {...fade(i)}
                className={`rounded-3xl border border-slate-100 bg-white p-8 shadow-[0_2px_20px_rgba(15,23,42,0.04)]
                            hover:shadow-xl hover:-translate-y-1 transition-all duration-300
                            ${i === 0 ? 'md:col-span-5' : i === 1 ? 'md:col-span-7' : 'md:col-span-12 md:flex md:items-center md:gap-10'}`}>
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                  style={{ background: [P.primarySoft, P.secondarySoft, P.accentSoft][i] }}>
                  <w.icon className="w-5 h-5" style={{ color: [P.ink, P.secondary, P.accent][i] }} />
                </div>
                <div className="md:flex-1">
                  <p className="font-outfit text-xl font-semibold mt-5 md:mt-0 tracking-tight">{w.title}</p>
                  <p className="text-slate-300 mt-2.5 leading-relaxed">{w.line}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs */}
      {programs.length > 0 && (
        <section className="px-5 md:px-8 pb-20 md:pb-24" data-testid="portal-programs">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-outfit text-3xl md:text-4xl font-bold tracking-tighter text-center">
              Open right now
            </h2>
            <p className="text-slate-300 mt-3 text-center">
              Each one has its own code. Enter it above and you're in that programme.
            </p>
            <div className="grid md:grid-cols-2 gap-6 mt-12">
              {programs.map((p, i) => (
                <motion.div key={p.id} {...fade(i)} data-testid={`portal-program-${p.id}`}
                  className="rounded-3xl border border-slate-100 bg-white p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="min-w-0">
                      <p className="font-outfit text-xl font-semibold tracking-tight">{p.name}</p>
                      <p className="text-sm text-slate-300 mt-1">
                        {(p.program_type || '').replace(/_/g, ' ')}
                      </p>
                    </div>
                    <span className="ml-auto shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full"
                      style={{ background: P.accentSoft, color: P.onAccent === '#ffffff' ? P.accent : '#0f172a' }}>
                      {p.full ? 'full' : p.spaces_left != null ? `${p.spaces_left} places left` : 'open'}
                    </span>
                  </div>
                  {p.description && <p className="text-slate-300 mt-4 leading-relaxed">{p.description}</p>}
                  <div className="flex flex-wrap gap-x-5 gap-y-2 mt-5 text-sm text-slate-300">
                    {p.starts_at && (
                      <span className="inline-flex items-center gap-1.5">
                        <CalendarDays className="w-3.5 h-3.5" />
                        {p.starts_at.slice(0, 10)}{p.ends_at ? ` → ${p.ends_at.slice(0, 10)}` : ''}
                      </span>
                    )}
                    {(p.age_min || p.age_max) && (
                      <span className="inline-flex items-center gap-1.5">
                        <Users className="w-3.5 h-3.5" /> ages {p.age_min || '-'}-{p.age_max || '-'}
                      </span>
                    )}
                    {p.location && <span>{p.location}</span>}
                  </div>
                  <button onClick={() => { setCode(p.invite_code); join(p.invite_code); }}
                    data-testid={`portal-join-${p.id}`} disabled={p.full}
                    className="mt-6 inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-full
                               transition-transform hover:scale-[1.02] disabled:opacity-40"
                    style={{ background: P.primary, color: P.onPrimary }}>
                    Join with {p.invite_code} <ArrowRight className="w-4 h-4" />
                  </button>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Parents */}
      <section className="px-5 md:px-8 pb-24">
        <div className="max-w-5xl mx-auto rounded-[2.5rem] border border-slate-100 bg-slate-50 px-8 md:px-16 py-16 text-center">
          <ShieldCheck className="w-6 h-6 mx-auto" style={{ color: P.ink }} />
          <h2 className="font-outfit text-2xl md:text-3xl font-bold tracking-tighter mt-5">
            For parents and guardians
          </h2>
          <div className="grid md:grid-cols-3 gap-8 mt-10 text-left">
            {[['Nothing starts without you',
               'Under 13, no profile begins until a guardian says yes, and you can withdraw it at any time.'],
              ['You can see the plan',
               "You see their plan, progress and the decisions they're weighing. Their private reflections stay theirs."],
              ['From 13, the answers are theirs',
               'Teenagers get their own password. You stay the guardian on the account and hold the plan.']]
              .map(([t, l]) => (
                <div key={t}>
                  <p className="font-semibold tracking-tight">{t}</p>
                  <p className="text-slate-300 mt-2 text-sm leading-relaxed">{l}</p>
                </div>
              ))}
          </div>
          {b.career_center && (
            <p className="text-sm text-slate-300 mt-10" data-testid="portal-support">{b.career_center}</p>
          )}
        </div>
      </section>

      <footer className="border-t border-slate-100 py-10">
        <div className="max-w-6xl mx-auto px-5 md:px-8 flex flex-col items-center gap-3">
          <LogoBox src={b.logo_url} name={org.name} size="sm" className="shadow-none border-0" />
          <p className="text-sm text-slate-300">
            {b.powered_by_text || 'Powered by Agentic FuturePath AI'}
          </p>
        </div>
      </footer>
    </div>
  );
}
